import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { type FormEvent, useEffect, useState } from 'react';

const SLUG_REGEX = /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/;

interface Props {
  open: boolean;
  onClose: () => void;
  source: { name: string; slug: string | null } | null;
  isPending: boolean;
  onSubmit: (input: { name: string; slug: string }) => void;
}

/**
 * 파일 복제 — 원본의 활성 본문/변수를 새 파일의 v1 로 시드.
 * 같은 이름·slug 충돌은 즉시 노출 (서버 검증과 별개로 클라이언트도 1차 차단).
 */
export function PromptDuplicateModal({ open, onClose, source, isPending, onSubmit }: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  useEffect(() => {
    if (open && source) {
      setName(`${source.name} (사본)`);
      setSlug(source.slug ? `${source.slug}-copy` : '');
    }
  }, [open, source]);
  if (!source) return null;

  const slugInvalid = slug.trim() !== '' && !SLUG_REGEX.test(slug.trim());
  const slugMissing = slug.trim() === '';
  const canSubmit = name.trim() !== '' && !slugInvalid && !slugMissing && !isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), slug: slug.trim() });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="프롬프트 복제"
      description={`"${source.name}" 의 활성 본문/변수를 새 프롬프트의 v1 로 시드합니다.`}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button type="submit" form="rp-duplicate-form" disabled={!canSubmit}>
            {isPending ? '복제 중…' : '복제'}
          </Button>
        </div>
      }
    >
      <form id="rp-duplicate-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-dup-name">
            새 이름
          </label>
          <Input
            id="rp-dup-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-dup-slug">
            새 slug <span className="text-destructive">*</span>
          </label>
          <Input
            id="rp-dup-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="query.language-detect-copy"
            aria-invalid={slugInvalid}
          />
          <div
            className={`mt-0.5 text-[10px] ${
              slugInvalid ? 'text-destructive' : 'text-foreground-muted'
            }`}
          >
            {slugInvalid ? '형식이 맞지 않습니다.' : '영소문자/숫자/점/하이픈만 가능.'}
          </div>
        </div>
      </form>
    </Modal>
  );
}
